import { Injectable } from "@angular/core";
import { defer, finalize, Observable } from "rxjs";
import { HttpConcurrencyQueueFullError, type PendingHttpRequest } from "./concurrency.models";

export const HTTP_CONCURRENCY_LIMIT = 15;

@Injectable({ providedIn: "root" })
export class HttpConcurrencyScheduler {
    private readonly maxQueueSize = 10000;
    private readonly queue: PendingHttpRequest[] = [];
    private active = 0;
    private draining = false;

    schedule<T>(request: () => Observable<T>): Observable<T> {
        return new Observable<T>((subscriber) => {
            if (this.queue.length >= this.maxQueueSize) {
                subscriber.error(new HttpConcurrencyQueueFullError());
                return;
            }

            const pending: PendingHttpRequest = {
                start: () => {
                    if (subscriber.closed) return;
                    this.active++;
                    defer(request)
                        .pipe(
                            finalize(() => {
                                this.active--;
                                this.drain();
                            }),
                        )
                        .subscribe(subscriber);
                },
            };

            this.queue.push(pending);
            this.drain();

            return () => {
                const index = this.queue.indexOf(pending);
                if (index !== -1) this.queue.splice(index, 1);
            };
        });
    }

    private drain(): void {
        if (this.draining) return;
        this.draining = true;
        try {
            while (this.active < HTTP_CONCURRENCY_LIMIT && this.queue.length > 0) {
                this.queue.shift()?.start();
            }
        } finally {
            this.draining = false;
        }
    }
}
